import { useState } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { usePayments } from '@/hooks/usePayments';
import { METHOD_LABELS, STATUS_LABELS, updatePaymentStatus } from '@/services/payments';
import { downloadCsv } from '@/lib/csv';
import PaymentModal from './components/PaymentModal';

function formatDate(value: string): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('es-CO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export default function PaymentsPage() {
  const { data, loading, error, refetch } = usePayments();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showNew, setShowNew] = useState(false);
  const [actionError, setActionError] = useState('');

  async function handleStatusChange(id: string, status: string) {
    setActionError('');
    const { error: updateError } = await updatePaymentStatus(id, status);
    if (updateError) setActionError(updateError);
    refetch();
  }

  const statuses = ['all', ...Array.from(new Set(data.map((p) => p.status)))];

  const approvedTotal = data.filter((p) => p.status === 'approved').reduce((s, p) => s + p.amount, 0);
  const pendingTotal = data
    .filter((p) => p.status === 'pending' || p.status === 'initiated')
    .reduce((s, p) => s + p.amount, 0);

  const filtered = data.filter((p) => {
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      p.paymentNumber.toLowerCase().includes(q) ||
      p.saleNumber.toLowerCase().includes(q) ||
      p.orderNumber.toLowerCase().includes(q) ||
      p.transactionRef.toLowerCase().includes(q)
    );
  });

  function handleExport() {
    downloadCsv(
      'pagos',
      ['Número', 'Venta', 'Pedido', 'Método', 'Estado', 'Monto', 'Referencia', 'Fecha'],
      filtered.map((p) => [
        p.paymentNumber,
        p.saleNumber,
        p.orderNumber,
        METHOD_LABELS[p.method] ?? p.method,
        STATUS_LABELS[p.status] ?? p.status,
        p.amount,
        p.transactionRef,
        formatDate(p.createdAt),
      ]),
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 space-y-5 max-w-[1400px] mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-foreground-950">Pagos</h1>
            <p className="text-sm text-foreground-500 mt-0.5 flex items-center flex-wrap gap-2">
              <span>{data.length} pagos registrados</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium whitespace-nowrap">
                Supabase
              </span>
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleExport}
              className="text-sm px-4 py-2.5 border border-background-300 text-foreground-700 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap font-medium cursor-pointer"
            >
              <i className="ri-download-line mr-1.5"></i>Exportar
            </button>
            <button
              onClick={() => setShowNew(true)}
              className="text-sm px-4 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap font-medium cursor-pointer"
            >
              <i className="ri-add-line mr-1.5"></i>Nuevo Pago
            </button>
          </div>
        </div>

        {actionError && (
          <div className="flex items-center gap-2 text-sm bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2.5">
            <i className="ri-error-warning-line"></i>
            <span>{actionError}</span>
          </div>
        )}

        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Total aprobado</p>
            <p className="text-xl font-bold text-emerald-600 mt-1">${approvedTotal.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Pendiente</p>
            <p className="text-xl font-bold text-amber-600 mt-1">${pendingTotal.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Total transacciones</p>
            <p className="text-xl font-bold text-foreground-950 mt-1">{data.length}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex flex-wrap gap-1.5">
            {statuses.map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  statusFilter === s
                    ? 'bg-accent-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {s === 'all' ? 'Todos' : STATUS_LABELS[s] ?? s}
              </button>
            ))}
          </div>
          <div className="relative flex-1 max-w-xs ml-auto">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              type="text"
              placeholder="Buscar por número, venta o referencia..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-700 focus:outline-none focus:ring-2 focus:ring-accent-300"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-100 text-left bg-background-50">
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Número</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Venta / Pedido</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Método</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right">Monto</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-center">Estado</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Fecha</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-10 text-center text-foreground-400">
                      <i className="ri-loader-4-line animate-spin mr-2"></i>Cargando pagos...
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <i className="ri-wifi-off-line text-3xl text-foreground-400"></i>
                        <div>
                          <p className="text-sm font-medium text-foreground-800">No se pudieron cargar los pagos</p>
                          <p className="text-xs text-foreground-500 mt-1 max-w-sm mx-auto">{error}</p>
                        </div>
                        <button
                          onClick={refetch}
                          className="text-sm px-4 py-2 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap font-medium cursor-pointer"
                        >
                          <i className="ri-refresh-line mr-1.5"></i>Reintentar
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filtered.map((p) => (
                    <tr key={p.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-accent-50 flex items-center justify-center flex-shrink-0">
                            <i className="ri-bank-card-line text-accent-600"></i>
                          </div>
                          <p className="text-foreground-900 font-medium whitespace-nowrap">{p.paymentNumber}</p>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-foreground-600 whitespace-nowrap">
                        {p.saleNumber || p.orderNumber || '—'}
                      </td>
                      <td className="px-5 py-3 text-foreground-600 whitespace-nowrap">{METHOD_LABELS[p.method] ?? p.method}</td>
                      <td className={`px-5 py-3 text-right font-semibold whitespace-nowrap ${p.amount < 0 ? 'text-red-600' : 'text-foreground-950'}`}>
                        ${p.amount.toLocaleString()}
                      </td>
                      <td className="px-5 py-3 text-center">
                        <select
                          value={p.status}
                          onChange={(e) => handleStatusChange(p.id, e.target.value)}
                          className="text-xs px-2 py-1 rounded-full font-medium border-0 bg-background-100 text-foreground-700 cursor-pointer"
                        >
                          {Object.keys(STATUS_LABELS).map((s) => (
                            <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-5 py-3 text-xs text-foreground-500 whitespace-nowrap">{formatDate(p.createdAt)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {!loading && !error && filtered.length === 0 && (
            <div className="text-center py-12 text-foreground-500">
              <i className="ri-bank-card-line text-3xl block mb-2"></i>
              <p className="text-sm">
                {data.length === 0
                  ? 'Aún no hay pagos registrados.'
                  : 'No se encontraron pagos con ese filtro.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {showNew && <PaymentModal onClose={() => setShowNew(false)} onSaved={refetch} />}
    </AdminLayout>
  );
}